import React, { useState } from 'react';
import usePlaces from './hooks/usePlaces';
import { Rating } from '@mui/material';
import Select from 'react-select';
import './App.css';

// Define the structure of the Place object
interface Place {
  name: string;
  rating: number;
  vicinity: string;
  icon: string;
  url: string;
}

interface PlaceTypeOption {
  value: string;
  label: string;
}

interface PlaceCategory {
  label: string;
  options: PlaceTypeOption[];
}

const App = () => {
  const [keyword, setKeyword] = useState('');
  const [radius, setRadius] = useState('');
  const [type, setType] = useState('');
  const [shouldSearch, setShouldSearch] = useState(false);
  const [randomPlace, setRandomPlace] = useState<Place | null>(null); // Explicitly set the type for randomPlace

  const { places, error } = usePlaces(keyword, radius, type, shouldSearch);

  const handleSearchClick = () => {
    setRandomPlace(null);
    setShouldSearch(true);
    setTimeout(() => setShouldSearch(false), 1000);
  };

  const handleRandomClick = () => {
    if (places && places.length > 0) {
      const randomIndex = Math.floor(Math.random() * places.length);
      setRandomPlace(places[randomIndex]); // Select a random place
    } else {
      setRandomPlace(null); // Reset if no places are found
    }
  };

  // Function to get the photo URL using the photo_reference
  const getPhotoUrl = (photoReference: string) => {
    const API_KEY = 'AIzaSyBzxo0Ekqnp4ynXyXqqmltaxj-RN_AKPqw'; // Replace with your real API key
    return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photo_reference=${photoReference}&key=${API_KEY}`;
  };

  const handleChange = (selectedOption: PlaceTypeOption | null) => {
    setType(selectedOption?.value || ""); // Store only the value (e.g., "restaurant")
  };

  return (
    <div className="app-container">
      <h1 className="app-title">Where should we go?</h1>

      <div className="search-container">
      <input
          className="input"
          type="text"
          placeholder="What you want (e.g., coffee)"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          required={true}
        />
        <Select
        className="react-select-container"
        classNamePrefix="react-select"
        options={placeCategories}
        isSearchable
        value={placeCategories
          .flatMap((category) => category.options)
          .find((option) => option.value === type) || null}
        onChange={handleChange}
        placeholder="Select a place type..."
      />

        <input
          className="input"
          type="number"
          placeholder="Radius in miles (default 3 miles)"
          value={radius}
          onChange={(e) => setRadius(e.target.value)}
          required={true}
        />
      </div>

      <div className="button-container">
        <button className="button" onClick={handleSearchClick}>Search</button>
        <button className="button" onClick={handleRandomClick}>Choose For Me</button>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="results-container">
        {randomPlace ? (
          <div className="random-place-container">
            <h3>Chosen Place</h3>
            <hr></hr>
            <a href={'https://google.com/search?q=' + randomPlace.name} target="_blank" rel="noopener noreferrer" className="place-link random-place-container">
            <p className="place-name name">{randomPlace.name}</p>
            <p>{randomPlace.vicinity}</p>
            <Rating value={randomPlace.rating} readOnly={true} className='rating' precision={0.2}/>
            </a>
          </div>
        ) : (
          <ul className="places-list">
            {places && places.length > 0 ? (
              places.map((place: Place, index: number) => (
                <li key={index} className="place-item">
                  <a href={'https://google.com/search?q=' + place.name} target="_blank" rel="noopener noreferrer" className="place-link">
                  <div className='icon-name'>
                    <img src={place.icon} width='15px' height='15px'/>
                    <h3 className='name'>{place.name}</h3>
                  </div>
                  <p>{place.vicinity}</p>
                  <Rating value={place.rating} readOnly={true} className='rating' precision={0.2}/>
                  </a>
                </li>
              ))
            ) : (
              <p>Click "Search" to load places...</p>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

const placeCategories: PlaceCategory[] = [
  {
    label: "Food & Drink",
    options: [
      { value: "restaurant", label: "Restaurant" },
      { value: "cafe", label: "Cafe" },
      { value: "bakery", label: "Bakery" },
      { value: "bar", label: "Bar" },
      { value: "fast_food_restaurant", label: "Fast Food" },
      { value: "coffee_shop", label: "Coffee Shop" },
      { value: "ice_cream_shop", label: "Ice Cream Shop" },
    ],
  },
  {
    label: "Entertainment & Recreation",
    options: [
      { value: "amusement_park", label: "Amusement Park" },
      { value: "aquarium", label: "Aquarium" },
      { value: "bowling_alley", label: "Bowling Alley" },
      { value: "casino", label: "Casino" },
      { value: "movie_theater", label: "Movie Theater" },
      { value: "museum", label: "Museum" },
      { value: "night_club", label: "Night Club" },
      { value: "park", label: "Park" },
      { value: "zoo", label: "Zoo" },
    ],
  },
  {
    label: "Lodging",
    options: [
      { value: "hotel", label: "Hotel" },
      { value: "motel", label: "Motel" },
      { value: "bed_and_breakfast", label: "Bed & Breakfast" },
      { value: "campground", label: "Campground" },
      { value: "resort_hotel", label: "Resort" },
    ],
  },
  {
    label: "Transportation",
    options: [
      { value: "airport", label: "Airport" },
      { value: "bus_station", label: "Bus Station" },
      { value: "train_station", label: "Train Station" },
      { value: "subway_station", label: "Subway Station" },
      { value: "taxi_stand", label: "Taxi Stand" },
      { value: "parking", label: "Parking" },
      { value: "gas_station", label: "Gas Station" },
    ],
  },
  {
    label: "Health & Wellness",
    options: [
      { value: "hospital", label: "Hospital" },
      { value: "pharmacy", label: "Pharmacy" },
      { value: "doctor", label: "Doctor" },
      { value: "dentist", label: "Dentist" },
      { value: "spa", label: "Spa" },
      { value: "gym", label: "Gym" },
      { value: "veterinary_care", label: "Veterinary Care" },
    ],
  },
  {
    label: "Shopping",
    options: [
      { value: "shopping_mall", label: "Shopping Mall" },
      { value: "grocery_store", label: "Grocery Store" },
      { value: "clothing_store", label: "Clothing Store" },
      { value: "jewelry_store", label: "Jewelry Store" },
      { value: "furniture_store", label: "Furniture Store" },
      { value: "shoe_store", label: "Shoe Store" },
      { value: "electronics_store", label: "Electronics Store" },
    ],
  },
];


export default App;
